/**
 * ItemListView — the primary item list page container.
 *
 * High-density, power-user list view with search, filtering,
 * grouping, sorting, density modes, virtual scrolling, and
 * a slide-in right panel for triage / metadata enrichment.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Library, AlertCircle, Loader2, Zap, Inbox, Check, Clock, HelpCircle, XCircle } from 'lucide-react';
import { shallow } from 'zustand/shallow';

import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useSpaceStore } from '../../../stores/prioritization/spaceStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { listWorkspaceClassificationHistory } from '../../../lib/itemClassificationService';
import { getItemCategoryLabel } from '../TrackCellBadge';
import { classificationMark, type ValueMarkNames } from '../../../lib/valueMark';
import { getAgentDefinition } from '../../../lib/ai/agentDefinitions';
import type { ItemClassificationProposal } from '../../../types/prioritization/itemClassification';
import type { ClassificationMarks } from './components/ItemTable';
import { useUserNameMap } from '../../../hooks/useUserNameMap';
import { itemIsAwaitingTriage, itemNeedsDefinition, triageFinalizedOutOfQueueIds } from '../../../lib/intakeQueueVisibility';
import { describeSpaceScope, scopeItemsToSpace } from '../../../lib/itemSpaceScope';
import { useItemSpaceScope } from '../../../hooks/prioritization/useItemSpaceScope';
import { useBulkAgentAsk } from '../../../hooks/useBulkAgentAsk';
import { bulkAskResultLine } from '../../../lib/bulkAgentAsk';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { approveAiItemRpc } from '../../../lib/itemProvenanceRpc';
import { RetireItemsDialog } from '../../reversals/RetireItemsDialog';

import type { ItemRow } from './hooks/useItemListData';
import { useItemQueryWorkspace } from './hooks/useItemQueryWorkspace';
import { EntityListShell, EntityListBody } from '../../../components/collection';
import { ItemQueryBar, ItemQueryRows, ItemQueryTable } from './components/ItemQueryWorkspace';
import { ItemCardGrid } from './components/ItemCardGrid';
import { BulkActionsBar, BULK_BAR_MIN_SELECTED, type BulkAct } from './components/BulkActionsBar';
import {
  BulkAssignOwnerModal,
  BulkTagsModal,
  BulkChangeQueueModal,
  BulkChangeStateModal,
  BulkAddToSessionModal,
  BulkSetFieldModal,
  BulkResultsDialog,
  itemsWord,
  type BulkActionResult,
} from '../bulk';
import { ChartSummary } from './components/ChartSummary';
import { coerceChartType, type ChartType } from './components/chartTypes';
import { SavedReportsPanel } from '../../../components/prioritization/reports/SavedReportsPanel';
import { CreateItemModal } from './components/CreateItemModal';

import type { SavedReport } from '../../../types/prioritization/savedReports';
import { ItemQuickPanel } from '../item-view/ItemQuickPanel';
import { ItemImportExportModal } from './ItemImportExportModal';
import { SpreadsheetPageDropTarget } from '../import-export/SpreadsheetPageDropTarget';
import { useTriageStore } from '../../../stores/prioritization/triageStore';
import { createEmptyQuickChecks } from '../../../types/prioritization/triage';
import type { TriageOutcome } from '../../../types/prioritization/triage';
import { useItemHref } from '@/lib/itemLinkScope';

/** A date for a CSV cell: YYYY-MM-DD, or empty when there is none. */
function csvDate(value: unknown): string {
  if (!value) return '';
  const d = new Date(value as string | number | Date);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/** Display modes that change which items are shown and which columns appear */
export type ItemListViewMode = 'all' | 'triage' | 'needs-shaping';

/** The bulk acts that open a dialog (export and retire have their own paths). */
type BulkDialog = Exclude<BulkAct, 'export' | 'retire'>;

// ============================================================================
// Component
// ============================================================================

export function ItemListView({ viewMode = 'all' }: { viewMode?: ItemListViewMode }) {
  const itemHref = useItemHref();
  const navigate = useNavigate();
  const can = useUserStore((s) => s.can);
  // can() reads the store, not React state: follow permissionsLoaded so this redraws when rights arrive.
  useUserStore((s) => s.permissionsLoaded);
  const canCreate = can('items.create');
  const canApproveAiProvenance = can('items.approve_ai_provenance');
  const canEditItems = can('items.edit');
  const canFacilitate = can('session.facilitate');

  // Stores
  const items = useItemsStore((s) => s.items);
  const fetchFromSupabase = useItemsStore((s) => s.fetchFromSupabase);
  const syncState = useItemsStore((s) => s.syncState);
  const updateItem = useItemsStore((s) => s.updateItem);
  const loadItemById = useItemsStore((s) => s.loadItemById);
  const addNotification = useUIStore((s) => s.addNotification);
  const userId = useUserStore((s) => s.userId);

  // Bumped when an agent asked from the bulk bar finishes, so the marks it may have changed are read again.
  const [agentRunsDone, setAgentRunsDone] = useState(0);

  // Bulk "Approve AI items" run
  const [approveAiRunning, setApproveAiRunning] = useState(false);
  const [approveAiProgress, setApproveAiProgress] = useState<{ done: number; total: number } | undefined>();

  // The space the list is scoped to comes from useItemSpaceScope (below); this
  // subscription only resets the "show all" toggle when the space changes.
  const currentSpaceId = useSpaceStore((s) => s.currentSpaceId);
  const [showAllSpaces, setShowAllSpaces] = useState(false);

  // Reset the "show all" toggle whenever the user switches spaces
  useEffect(() => {
    setShowAllSpaces(false);
  }, [currentSpaceId]);

  // Panel and focus mode state
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [focusModeActive, setFocusModeActive] = useState(false);
  const [focusModeIndex, setFocusModeIndex] = useState(0);
  const [archiveConfirmItem, setArchiveConfirmItem] = useState<ItemRow | null>(null);
  const [retireTargets, setRetireTargets] = useState<Array<{ id: string; title?: string }> | null>(null);

  // Triage store (for focus mode quick actions)
  const submitTriageAssessment = useTriageStore((s) => s.submitTriageAssessment);
  const triageAssessments = useTriageStore((s) => s.assessments, shallow);

  // Creator + owner names — one shared lookup (useUserNameMap), the same one
  // the custom report page uses, so both pages name the same people.
  // Who decided each item's category and altitude (SPEC-value-mark-icon): the
  // workspace's classification history, read once.
  const [classHistory, setClassHistory] = useState<ItemClassificationProposal[]>([]);
  const classWorkspaceId = getCurrentWorkspaceId();
  useEffect(() => {
    if (!classWorkspaceId) return;
    let live = true;
    void listWorkspaceClassificationHistory(classWorkspaceId).then((r) => live && setClassHistory(r.data));
    return () => {
      live = false;
    };
  }, [classWorkspaceId, items.length, agentRunsDone]);

  const nameMap = useUserNameMap([
    ...items.flatMap((item) => [
      (item as { createdByUserId?: string }).createdByUserId ?? item.createdBy,
      item.ownerId,
    ]),
    ...classHistory.map((p) => p.acceptedByUserId),
  ]);
  const creatorNameMap = nameMap;
  const ownerNameMap = nameMap;

  const classificationMarks = useMemo(() => {
    const out: Record<string, ClassificationMarks> = {};
    if (classHistory.length === 0) return out;
    // R-3: a mark only where an AI can judge — here, a workspace where an agent has classified.
    const aiCanJudge = classHistory.some((p) => p.proposedBy === 'agent');
    const names: ValueMarkNames = {
      nameOf: (id) => (id ? nameMap[id] ?? null : null),
      agentName: (t) => (t ? getAgentDefinition(t)?.name ?? t : 'The agent'),
    };
    for (const id of new Set(classHistory.map((p) => p.itemId))) {
      out[id] = {
        category: classificationMark(id, 'category', classHistory, aiCanJudge, names, (v) => getItemCategoryLabel(String(v)) ?? String(v)),
        altitude: classificationMark(id, 'altitude', classHistory, aiCanJudge, names),
      };
    }
    return out;
  }, [classHistory, nameMap]);

  // Fetch items on mount
  useEffect(() => {
    fetchFromSupabase();
  }, [fetchFromSupabase]);

  // Search ref for Cmd+K focus
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Space-scoped pre-filter: the one rule in lib/itemSpaceScope.ts, shared
  // with the phone item list and the Pipeline Health report so a count on a
  // report is the count of the list it opens.
  const spaceScope = useItemSpaceScope();
  const spaceFilteredItems = useMemo(
    () => scopeItemsToSpace(items, spaceScope, { showAllSpaces }),
    [items, spaceScope, showAllSpaces],
  );

  // viewMode-filtered items (applied before the query)
  const viewModeFilteredItems = useMemo(() => {
    switch (viewMode) {
      case 'triage': {
        const triagedOut = triageFinalizedOutOfQueueIds(triageAssessments);
        return spaceFilteredItems.filter((item) => itemIsAwaitingTriage(item, triagedOut));
      }
      case 'needs-shaping':
        // Same predicate as the sidebar badge and the Pipeline Health funnel.
        return spaceFilteredItems.filter((item) => itemNeedsDefinition(item));
      default:
        return spaceFilteredItems;
    }
  }, [spaceFilteredItems, viewMode, triageAssessments]);

  // The shared list block (ItemQueryWorkspace): the query bar, filters,
  // columns and rows — the same block the Report Designer and the Item Pool
  // Editor draw. The statement lives on the address (`pql`).
  const ws = useItemQueryWorkspace({ items: viewModeFilteredItems });
  const {
    searchQuery,
    setSearchQuery,
    sort,
    setSort,
    groupBy,
    setGroupBy,
    density,
    setDensity,
    filterGroups,
    filterCount,
    addGroup,
    addRule,
    clearFilters,
    visibleColumns,
    toggleColumn,
    reorderColumns,
    expandedGroups,
    toggleGroup,
    toggleCluster,
    selectedRows,
    selectAllRows,
    setRowsSelected,
    clearSelection,
    layoutMode,
    setLayoutMode,
    allColumns,
    statement,
    rows,
    displayRows,
    grouped,
    totalCount,
    filteredCount,
    queueNameMap,
  } = ws;

  // Reports panel
  const [reportsPanelOpen, setReportsPanelOpen] = useState(false);

  const applyStatement = statement.applyStatement;
  // The saved query currently open, if any — so a rule derived from these
  // results can record where it was copied from. Only the id and the text it
  // was applied with: the "Use results" verbs compare that text against what
  // is in the bar now, and treat an edited statement as having no origin.
  const [appliedReport, setAppliedReport] = useState<{ id: string; queryText: string } | null>(null);
  const handleApplyReport = useCallback((report: SavedReport) => {
    if (report.queryText) {
      // A saved query carries a full statement (possibly negation/nesting the
      // builder can't show) — it re-opens in statement mode, as written.
      applyStatement(report.queryText);
    } else if (report.filterGroups) {
      // Replace all filter groups
      clearFilters();
      report.filterGroups.forEach((g) => {
        // Add each group's rules
        if (g.rules.length > 0) {
          addGroup();
          // Note: we call addRule for each rule; the last-added group receives them
          g.rules.forEach(({ id: _ruleId, ...rule }) => {
            void _ruleId;
            addRule(rule);
          });
        }
      });
    }
    if (report.sort?.length) setSort(report.sort);
    if (report.groupBy !== undefined) setGroupBy(report.groupBy);
    if (report.activeChart) setActiveChart(coerceChartType(report.activeChart));
    setAppliedReport(report.queryText ? { id: report.id, queryText: report.queryText } : null);
  }, [clearFilters, addGroup, addRule, setSort, setGroupBy, applyStatement]);

  // Create item modal
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Import/Export modals
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [droppedSpreadsheet, setDroppedSpreadsheet] = useState<File | null>(null);

  const handleSpreadsheetDrop = useCallback((file: File) => {
    setDroppedSpreadsheet(file);
    setShowImportModal(true);
  }, []);

  const handleUnsupportedDrop = useCallback((file: File) => {
    addNotification({
      type: 'error',
      title: 'Unsupported file',
      message: `"${file.name}" is not a CSV or Excel file.`,
      duration: 4000,
    });
  }, [addNotification]);

  // Chart visibility (persisted to localStorage)
  const [showChart, setShowChart] = useState(() => {
    try { return localStorage.getItem('itemListShowChart') !== 'false'; } catch { /* ignore */ return true; }
  });
  const toggleChart = useCallback(() => {
    setShowChart((prev) => {
      const next = !prev;
      try { localStorage.setItem('itemListShowChart', String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Active chart type (persisted)
  const [activeChart, setActiveChart] = useState<ChartType>(() => {
    try { return coerceChartType(localStorage.getItem('itemListActiveChart')); } catch { /* ignore */ return 'category'; }
  });
  const handleChartTypeChange = useCallback((type: ChartType) => {
    setActiveChart(type);
    try { localStorage.setItem('itemListActiveChart', type); } catch { /* ignore */ }
  }, []);

  // "Ask [agent] for these N" — the agents the chosen rows can be given to (O2, the bulk half).
  const selectedIdList = useMemo(() => Array.from(selectedRows), [selectedRows]);
  const bulkAsk = useBulkAgentAsk(selectedIdList, selectedRows.size >= BULK_BAR_MIN_SELECTED);

  // Handlers
  // Clicking the TITLE CELL opens Item Detail (see ItemTable's `onTitleClick`).
  // Clicking anywhere else in the row does nothing on purpose: the other cells
  // are for reading and for editing in place, and a row-wide navigation handler
  // threw you out of the list on any stray click.
  //
  // The quick panel is deprecated (DEP-028) but deliberately not deleted — still
  // reachable from the row action menu. Triage status now shows on Item Detail
  // (ItemTriageStatus), so the one thing the panel uniquely showed is no longer
  // trapped there.
  const handleRowClick = useCallback(() => {}, []);

  const handleTitleClick = useCallback(
    (item: ItemRow) => {
      // A cluster entry has no detail page of its own — its title expands it.
      if (item.clusterEntry) {
        toggleCluster(item.clusterEntry.groupId);
        return;
      }
      navigate(itemHref(item.id));
    },
    [navigate, toggleCluster, itemHref]
  );

  const handleRowAction = useCallback(
    (action: string, item: ItemRow) => {
      switch (action) {
        case 'view':
          navigate(itemHref(item.id));
          break;
        case 'open-panel':
          setSelectedItemId(item.id);
          break;
        case 'copy-link':
          navigator.clipboard.writeText(`${window.location.origin}/prioritization/items/${item.id}`);
          addNotification({ type: 'success', title: 'Link copied', message: 'Item link copied to clipboard.', duration: 2000 });
          break;
        case 'defer':
          updateItem(item.id, { exit_status: 'deferred' });
          addNotification({ type: 'success', title: 'Item deferred', message: `"${item.title}" has been deferred.`, duration: 3000 });
          break;
        case 'archive':
          setArchiveConfirmItem(item);
          break;
        case 'triage:pursue':
          void submitTriageAssessment(item.id, { itemId: item.id, quickChecks: createEmptyQuickChecks(), outcome: 'pursue' });
          addNotification({ type: 'success', title: 'Pursued', message: `"${item.title}" accepted and now active.`, duration: 3000 });
          break;
        case 'triage:defer':
          void submitTriageAssessment(item.id, { itemId: item.id, quickChecks: createEmptyQuickChecks(), outcome: 'defer' });
          addNotification({ type: 'success', title: 'Deferred', message: `"${item.title}" has been deferred.`, duration: 3000 });
          break;
        case 'triage:reject':
          void submitTriageAssessment(item.id, { itemId: item.id, quickChecks: createEmptyQuickChecks(), outcome: 'reject' });
          addNotification({ type: 'success', title: 'Archived', message: `"${item.title}" has been archived.`, duration: 3000 });
          break;
      }
    },
    [navigate, addNotification, updateItem, submitTriageAssessment, itemHref]
  );

  // Bulk dialogs. The bar names an act; its dialog shows what is about to
  // happen, applies it one item at a time and reports what saved. All good →
  // a toast and the selection clears. Some refused → the failed items stay
  // selected and the results dialog says which and why.
  const [bulkDialog, setBulkDialog] = useState<BulkDialog | null>(null);
  const [bulkIds, setBulkIds] = useState<string[]>([]);
  const [bulkResult, setBulkResult] = useState<BulkActionResult | null>(null);

  const handleBulkComplete = useCallback(
    (result: BulkActionResult) => {
      setBulkDialog(null);
      if (result.failed.length === 0) {
        clearSelection();
        addNotification({
          type: 'success',
          title: result.action,
          message: `${itemsWord(result.succeeded)} updated.`,
          duration: 3000,
        });
        return;
      }
      selectAllRows(result.failed.map((f) => f.itemId));
      setBulkResult(result);
    },
    [clearSelection, selectAllRows, addNotification]
  );

  const handleBulkAct = useCallback(
    (act: BulkAct) => {
      const ids = Array.from(selectedRows);
      switch (act) {
        case 'export': {
          const exportItems = items.filter((i) => ids.includes(i.id));
          const csv = [
            'id,title,category,status,altitude,created',
            ...exportItems.map((i) =>
              [i.id, `"${i.title?.replace(/"/g, '""') ?? ''}"`, i.itemCategory ?? '', i.exit_status ?? 'active', i.altitudeLevel ?? '', csvDate(i.createdAt)].join(',')
            ),
          ].join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `items-export-${new Date().toISOString().slice(0, 10)}.csv`;
          a.click();
          URL.revokeObjectURL(url);
          clearSelection();
          addNotification({ type: 'success', title: 'Exported', message: `${itemsWord(ids.length)} exported to CSV.`, duration: 3000 });
          break;
        }
        case 'retire': {
          // "Delete" is Retire now (owner ruling 2026-09-22): one reason, the
          // governed retire_new_item per item, honest N-of-M reporting inside
          // the dialog, and the archive path for items already in use.
          setRetireTargets(items.filter((i) => ids.includes(i.id)).map((i) => ({ id: i.id, title: i.title })));
          break;
        }
        default:
          setBulkIds(ids);
          setBulkDialog(act);
      }
    },
    [selectedRows, items, clearSelection, addNotification]
  );

  const { ask: askAgent } = bulkAsk;
  const handleAskAgent = useCallback(
    async (agentType: string) => {
      const outcome = await askAgent(agentType);
      if (!outcome) return;
      if (outcome.status === 'running') {
        addNotification({ type: 'info', title: `${outcome.name} is already running`, message: 'Ask again when it finishes.', duration: 4000 });
        return;
      }
      clearSelection();
      setAgentRunsDone((n) => n + 1);
      void fetchFromSupabase();
      const ok = outcome.status === 'completed' && (outcome.stats?.errors ?? 0) === 0;
      addNotification({
        type: ok ? 'success' : 'warning',
        title: outcome.name,
        message: bulkAskResultLine(outcome.name, outcome.stats, outcome.status, outcome.run),
        duration: 6000,
      });
    },
    [askAgent, addNotification, clearSelection, fetchFromSupabase]
  );

  const handleBulkApproveAi = useCallback(async () => {
    if (approveAiRunning || !userId) return;
    const ids = Array.from(selectedRows);
    const aiDraftItems = items.filter((i) => ids.includes(i.id) && i.creationProvenance === 'ai_draft');
    if (aiDraftItems.length === 0) return;

    setApproveAiRunning(true);
    setApproveAiProgress({ done: 0, total: aiDraftItems.length });

    let approved = 0;
    const failures: string[] = [];

    for (const item of aiDraftItems) {
      const result = await approveAiItemRpc(item.id, userId);
      if (result.success) {
        approved++;
        // Refresh from the server rather than a direct client write — the
        // creation_provenance transition is an authority transition and must
        // go through approve_ai_item, not itemsStore.updateItem's direct write.
        await loadItemById(item.id);
      } else {
        failures.push(`"${item.title}": ${result.error ?? 'unknown error'}`);
      }
      setApproveAiProgress({ done: approved + failures.length, total: aiDraftItems.length });
    }

    setApproveAiRunning(false);
    setApproveAiProgress(undefined);
    clearSelection();

    addNotification({
      type: failures.length === 0 ? 'success' : 'warning',
      title: 'Marked as reviewed',
      message: `${approved} item${approved !== 1 ? 's' : ''} marked as reviewed${
        failures.length > 0 ? `. ${failures.length} failed: ${failures.join('; ')}` : '.'
      }`,
      duration: failures.length > 0 ? 7000 : 5000,
    });
  }, [approveAiRunning, userId, selectedRows, items, loadItemById, addNotification, clearSelection]);

  const approveAiAvailable = useMemo(
    () =>
      canApproveAiProvenance &&
      Array.from(selectedRows).some((id) => items.find((i) => i.id === id)?.creationProvenance === 'ai_draft'),
    [canApproveAiProvenance, selectedRows, items]
  );

  // Chart filter handler — clicking a chart segment adds a rule to the last group (or new group)
  const handleChartFilter = useCallback(
    (field: string, value: string) => {
      // Don't add duplicate rule for same field+value
      const allRules = filterGroups.flatMap((g) => g.rules);
      const existing = allRules.find(
        (r) => r.field === field && (Array.isArray(r.value) ? r.value.includes(value) : r.value === value)
      );
      if (existing) return;

      const labelMap: Record<string, string> = {
        exit_status: 'Status',
        detailedState: 'Status',
        itemCategory: 'Category',
        altitude: 'Altitude',
      };
      const displayMap: Record<string, Record<string, string>> = {
        exit_status: { active: 'Active', deferred: 'Deferred', archived: 'Archived' },
        detailedState: { active: 'Active', deferred: 'Deferred', archived: 'Archived' },
      };
      addRule({
        field,
        label: labelMap[field] || field,
        operator: 'in',
        value: [value],
        displayValue: displayMap[field]?.[value] || value.charAt(0).toUpperCase() + value.slice(1),
      });
    },
    [filterGroups, addRule]
  );

  // Inject triage_action column for triage mode (appended after visible columns)
  const effectiveColumns = useMemo(() => {
    if (viewMode === 'triage') {
      return visibleColumns.includes('triage_action')
        ? visibleColumns
        : [...visibleColumns, 'triage_action'];
    }
    return visibleColumns;
  }, [visibleColumns, viewMode]);

  // Loading state
  const isLoading = syncState === 'loading' && items.length === 0;

  // Focus mode handlers
  const handleFocusModeAction = useCallback(async (outcome: TriageOutcome) => {
    const currentItem = rows[focusModeIndex];
    if (!currentItem) return;
    await submitTriageAssessment(currentItem.id, {
      itemId: currentItem.id,
      quickChecks: createEmptyQuickChecks(),
      outcome,
    });
    // Advance to next
    const next = focusModeIndex + 1;
    if (next < rows.length) {
      setFocusModeIndex(next);
      setSelectedItemId(rows[next].id);
    } else {
      setFocusModeActive(false);
      setSelectedItemId(null);
    }
  }, [rows, focusModeIndex, submitTriageAssessment]);

  // Keyboard shortcuts for focus mode
  useEffect(() => {
    if (!focusModeActive) return;
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      switch (e.key) {
        case 'j': case 'ArrowDown': {
          const next = Math.min(focusModeIndex + 1, rows.length - 1);
          setFocusModeIndex(next);
          setSelectedItemId(rows[next]?.id ?? null);
          break;
        }
        case 'k': case 'ArrowUp': {
          const prev = Math.max(focusModeIndex - 1, 0);
          setFocusModeIndex(prev);
          setSelectedItemId(rows[prev]?.id ?? null);
          break;
        }
        case 'p': handleFocusModeAction('pursue'); break;
        case 'd': handleFocusModeAction('defer'); break;
        case 'r': handleFocusModeAction('reject'); break;
        case 'Escape': setFocusModeActive(false); break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusModeActive, focusModeIndex, rows, handleFocusModeAction]);

  // viewMode metadata for header
  const viewMeta = useMemo(() => {
    switch (viewMode) {
      case 'triage':        return { icon: <Inbox className="h-4 w-4 text-[var(--text-ter)]" />, iconBg: 'bg-[var(--slate-dim)]', title: 'Triage Queue',      sub: 'Review and route incoming intake items' };
      case 'needs-shaping': return { icon: <Zap className="h-4 w-4 text-[var(--text-ter)]" />, iconBg: 'bg-[var(--slate-dim)]', title: 'Needs Definition', sub: 'Items missing category or altitude level' };
      default:              return { icon: <Library className="h-4 w-4 text-[var(--text-ter)]" />, iconBg: 'bg-[var(--slate-dim)]', title: 'Items',          sub: 'All items in your workspace · Drop CSV or Excel anywhere to import' };
    }
  }, [viewMode]);

  const currentSort = sort[0] ?? { field: 'updatedAt', direction: 'desc' as const };

  const spaceWords = describeSpaceScope(spaceScope, { showAllSpaces });
  const spaceContextBar = spaceWords
    ? {
        label: spaceWords.label,
        action: { label: spaceWords.toggleLabel, onClick: () => setShowAllSpaces((v) => !v) },
      }
    : undefined;

  return (
    <>
    <SpreadsheetPageDropTarget
      enabled={viewMode === 'all' && canCreate && !showImportModal && !showExportModal}
      onFile={handleSpreadsheetDrop}
      onUnsupportedFile={handleUnsupportedDrop}
    />
    <EntityListShell
      className="h-[calc(100vh-6rem)] -mx-1"
      icon={viewMeta.icon}
      iconBgClassName={viewMeta.iconBg}
      title={viewMeta.title}
      subtitle={viewMeta.sub}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      searchInputRef={searchRef}
      onRefresh={() => void fetchFromSupabase()}
      onImport={viewMode === 'all' ? () => {
        setDroppedSpreadsheet(null);
        setShowImportModal(true);
      } : undefined}
      onExport={viewMode === 'all' ? () => setShowExportModal(true) : undefined}
      primaryAction={
        canCreate
          ? { label: 'New item', onClick: () => setCreateModalOpen(true) }
          : undefined
      }
      headerActions={
        viewMode === 'triage' && rows.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              const next = !focusModeActive;
              setFocusModeActive(next);
              if (next) {
                setFocusModeIndex(0);
                setSelectedItemId(rows[0]?.id ?? null);
              }
            }}
            className={`flex items-center gap-2 px-3 py-1.5  text-sm font-semibold transition-all ${
              focusModeActive
                ? 'bg-[var(--slate)] text-white shadow-sm shadow-indigo-200'
                : 'border border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
            }`}
          >
            <Zap className="h-3.5 w-3.5" />
            {focusModeActive ? 'Exit Focus Mode' : 'Focus Mode'}
          </button>
        ) : undefined
      }
      headerExtra={
        focusModeActive ? (
          <div className="flex items-center gap-3 px-4 py-2 bg-[var(--slate)] text-[var(--text-ter)] flex-shrink-0">
            <Zap className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--text-ter)]">Focus Mode</span>
            {rows[focusModeIndex] && (
              <span className="text-[12px] text-[var(--text-ter)] truncate max-w-[280px]">
                {rows[focusModeIndex].title}
              </span>
            )}
            <div className="flex items-center gap-1 ml-1">
              <div className="h-1 w-28 bg-[var(--slate)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[var(--slate)] rounded-full transition-all"
                  style={{ width: `${rows.length ? ((focusModeIndex + 1) / rows.length) * 100 : 0}%` }}
                />
              </div>
              <span className="text-[11px] text-[var(--text-ter)]">{focusModeIndex + 1} / {rows.length}</span>
            </div>
            <div className="flex gap-1.5 ml-auto">
              <button type="button" onClick={() => handleFocusModeAction('pursue')} className="px-2.5 py-1 rounded text-[11px] font-semibold bg-[var(--green)]/50 text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green)]/70 border border-[var(--green)]/50 transition"><Check className="inline h-3 w-3 mr-1" />Pursue <kbd className="opacity-60 ml-1">P</kbd></button>
              <button type="button" onClick={() => handleFocusModeAction('defer')} className="px-2.5 py-1 rounded text-[11px] font-semibold bg-[var(--mustard)]/50 text-[var(--mustard)] hover:bg-[var(--mustard)]/70 border border-[var(--mustard)]/50 transition"><Clock className="inline h-3 w-3 mr-1" />Defer <kbd className="opacity-60 ml-1">D</kbd></button>
              <button type="button" onClick={() => handleFocusModeAction('reject')} className="px-2.5 py-1 rounded text-[11px] font-semibold bg-[var(--coral)]/50 text-[var(--coral)] hover:bg-[var(--coral)]/70 border border-[var(--coral)]/50 transition"><XCircle className="inline h-3 w-3 mr-1" />Reject <kbd className="opacity-60 ml-1">R</kbd></button>
              <button type="button" onClick={() => handleFocusModeAction('needs_clarification')} className="px-2.5 py-1 rounded text-[11px] font-semibold bg-[var(--slate)]/50 text-[var(--text-ter)] hover:bg-[var(--slate)]/70 border border-[var(--slate)]/50 transition"><HelpCircle className="inline h-3 w-3 mr-1" />Info</button>
              <span className="text-[11px] text-[var(--text-ter)] ml-1 self-center">J/K navigate · Esc exit</span>
            </div>
          </div>
        ) : undefined
      }
      afterToolbar={
        <ItemQueryBar ws={ws} view={{ sort, groupBy, visibleColumns, activeChart }} appliedReport={appliedReport} />
      }
      contextBar={spaceContextBar}
      filterPanelOpen={false}
      toolbar={{
        density,
        onDensityChange: setDensity,
        sortOptions: [
          { field: 'updatedAt', label: 'Recently Updated' },
          { field: 'createdAt', label: 'Recently Created' },
          { field: 'title', label: 'Title (A–Z)' },
          { field: 'exit_status', label: 'Status' },
          { field: 'itemCategory', label: 'Category' },
          { field: 'strategicBucket', label: 'Strategic Bucket' },
          { field: 'altitude', label: 'Altitude' },
          { field: 'readiness', label: 'Readiness' },
          { field: 'queue', label: 'Queue' },
          { field: 'source', label: 'Source' },
        ],
        sort: { field: currentSort.field, direction: currentSort.direction },
        onSortChange: (s) => setSort([{ field: s.field, direction: s.direction }]),
        groupOptions: [
          { field: 'itemCategory', label: 'Category' },
          { field: 'exit_status', label: 'Status' },
          { field: 'altitude', label: 'Altitude' },
          { field: 'strategicBucket', label: 'Strategic Bucket' },
          { field: 'queue', label: 'Queue' },
          { field: 'collection', label: 'Collection' },
          { field: 'source', label: 'Source' },
        ],
        groupBy,
        onGroupByChange: setGroupBy,
        filterCount,
        // Opened in statement mode, the panel first takes the query into the
        // builder; a query it cannot show (NOT, nesting) stays in the bar.
        onToggleFilterPanel: ws.toggleFilters,
        totalCount,
        filteredCount,
        selectedCount: selectedRows.size,
        onSelectAllFiltered: () => selectAllRows(rows.map((r) => r.id)),
        layoutMode,
        onLayoutModeChange: setLayoutMode,
        showChart,
        onToggleChart: toggleChart,
        onManageReports: () => setReportsPanelOpen(true),
        columns: allColumns.map((c) => ({ key: c.key, label: c.label })),
        visibleColumns,
        onToggleColumn: toggleColumn,
        onReorderColumns: reorderColumns,
        itemLabel: 'item',
      }}
    >
      {showChart && rows.length > 0 && (
        <ChartSummary
          rows={rows}
          totalCount={totalCount}
          filteredCount={filteredCount}
          onFilterByField={handleChartFilter}
          activeChart={activeChart}
          onChartTypeChange={handleChartTypeChange}
        />
      )}

      {/* Bulk actions */}
      <BulkActionsBar
        selectedCount={selectedRows.size}
        totalCount={filteredCount}
        onSelectAll={() => selectAllRows(rows.map((r) => r.id))}
        onClear={clearSelection}
        onAct={handleBulkAct}
        allowed={{
          owner: canEditItems,
          tags: canEditItems,
          queue: canEditItems,
          status: canEditItems,
          field: canEditItems,
          session: canFacilitate,
          export: true,
          retire: true,
        }}
        agentAsk={{
          entries: bulkAsk.plan.entries,
          fix: bulkAsk.plan.fix,
          needsKey: bulkAsk.plan.needsKey,
          running: bulkAsk.running,
          onAsk: (agentType) => void handleAskAgent(agentType),
        }}
        onApproveAiItems={handleBulkApproveAi}
        approveAiAvailable={approveAiAvailable}
        approveAiRunning={approveAiRunning}
        approveAiProgress={approveAiProgress}
      />

      {/* Main content area */}
      <ItemQueryRows ws={ws}>
        <EntityListBody
          className="min-w-0"
          detailPanelOpen={Boolean(selectedItemId)}
          detailPanel={
            selectedItemId ? (
              <ItemQuickPanel
                itemId={selectedItemId}
                itemIds={rows.map((r) => r.id)}
                onClose={() => setSelectedItemId(null)}
                onNavigate={(id) => setSelectedItemId(id)}
                defaultTab={viewMode === 'triage' ? 'triage' : 'triage'}
              />
            ) : undefined
          }
        >
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-[var(--text-ter)]">
                <Loader2 className="h-8 w-8 animate-spin" />
                <p className="text-sm">Loading items...</p>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              hasFilters={filterCount > 0 || !!searchQuery}
              searchQuery={searchQuery}
              onClearFilters={() => { clearFilters(); setSearchQuery(''); }}
              totalCount={totalCount}
            />
          ) : layoutMode === 'card' ? (
            <ItemCardGrid
              rows={displayRows}
              grouped={grouped}
              sort={sort}
              expandedGroups={expandedGroups}
              onToggleGroup={toggleGroup}
              searchQuery={searchQuery}
              // A card has no title cell to click, so the card itself is the title:
              // it opens Item Detail (or expands a cluster), as the table's title does.
              onRowClick={handleTitleClick}
              selectedItemId={selectedItemId}
              queueNameMap={queueNameMap}
            />
          ) : (
            <ItemQueryTable
              ws={ws}
              onRowClick={handleRowClick}
              onTitleClick={handleTitleClick}
              onRowAction={handleRowAction}
              creatorNameMap={creatorNameMap}
              ownerNameMap={ownerNameMap}
              classificationMarks={classificationMarks}
              visibleColumns={effectiveColumns}
              selectedItemId={selectedItemId}
              viewMode={viewMode}
            />
          )}
        </EntityListBody>
      </ItemQueryRows>
    </EntityListShell>

    <SavedReportsPanel
      isOpen={reportsPanelOpen}
      onClose={() => setReportsPanelOpen(false)}
      onApplyReport={handleApplyReport}
      currentState={{
        filterGroups,
        sort,
        groupBy,
        visibleColumns,
        activeChart,
        ...(statement.mode === 'statement'
          ? { queryText: statement.canonicalText, queryAst: statement.appliedAst }
          : {}),
      }}
      exportRows={rows}
    />
    <CreateItemModal isOpen={createModalOpen} onClose={() => setCreateModalOpen(false)} />

    {showImportModal && (
      <ItemImportExportModal
        mode="import"
        items={items}
        initialFile={droppedSpreadsheet}
        onClose={() => {
          setShowImportModal(false);
          setDroppedSpreadsheet(null);
        }}
      />
    )}
    {showExportModal && (
      <ItemImportExportModal
        mode="export"
        items={items}
        onClose={() => setShowExportModal(false)}
      />
    )}

    {bulkDialog === 'owner' && (
      <BulkAssignOwnerModal open selectedIds={bulkIds} onClose={() => setBulkDialog(null)} onComplete={handleBulkComplete} />
    )}
    {bulkDialog === 'tags' && (
      <BulkTagsModal open selectedIds={bulkIds} onClose={() => setBulkDialog(null)} onComplete={handleBulkComplete} />
    )}
    {bulkDialog === 'queue' && (
      <BulkChangeQueueModal open selectedIds={bulkIds} onClose={() => setBulkDialog(null)} onComplete={handleBulkComplete} />
    )}
    {bulkDialog === 'status' && (
      <BulkChangeStateModal open selectedIds={bulkIds} onClose={() => setBulkDialog(null)} onComplete={handleBulkComplete} />
    )}
    {bulkDialog === 'session' && (
      <BulkAddToSessionModal open selectedIds={bulkIds} onClose={() => setBulkDialog(null)} onComplete={handleBulkComplete} />
    )}
    {bulkDialog === 'field' && (
      <BulkSetFieldModal open selectedIds={bulkIds} onClose={() => setBulkDialog(null)} onComplete={handleBulkComplete} />
    )}
    <BulkResultsDialog result={bulkResult} onClose={() => setBulkResult(null)} />

    <RetireItemsDialog
      open={retireTargets !== null}
      onClose={() => setRetireTargets(null)}
      items={retireTargets ?? []}
      // What was refused stays selected, like every other bulk act here.
      onFinished={({ failed }) => selectAllRows(failed.map((f) => f.id))}
      onArchived={(ids) => setRowsSelected(ids, false)}
    />
    {archiveConfirmItem && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] dark:border-[var(--rule)] max-w-sm w-full mx-4">
          <div className="p-6">
            <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-primary)]">Archive item?</h3>
            <p className="mt-2 text-sm text-[var(--text-ter)] dark:text-[var(--text-secondary)]">
              <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-primary)]">"{archiveConfirmItem.title}"</span> will be removed from active consideration. You can find archived items by filtering on status.
            </p>
          </div>
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--rule)] dark:border-[var(--rule)]">
            <button
              type="button"
              onClick={() => setArchiveConfirmItem(null)}
              className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-secondary)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                updateItem(archiveConfirmItem.id, { exit_status: 'archived' });
                addNotification({ type: 'success', title: 'Item archived', message: `"${archiveConfirmItem.title}" has been archived.`, duration: 3000 });
                setArchiveConfirmItem(null);
              }}
              className="px-4 py-2 text-sm font-medium bg-[var(--coral)] hover:bg-[var(--coral)] text-white transition-colors"
            >
              Archive
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ============================================================================
// Empty state
// ============================================================================

function EmptyState({
  hasFilters,
  searchQuery,
  onClearFilters,
  totalCount,
}: {
  hasFilters: boolean;
  searchQuery: string;
  onClearFilters: () => void;
  totalCount: number;
}) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-sm">
        {totalCount === 0 ? (
          <>
            <Library className="h-12 w-12 mx-auto mb-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
            <h3 className="text-lg font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">No items yet</h3>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Add one with New Item, or import a list. Ideas from intake appear here once they are accepted in triage.
            </p>
          </>
        ) : hasFilters ? (
          <>
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
            <h3 className="text-lg font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
              {searchQuery
                ? `No items match "${searchQuery}"`
                : 'No items match your current filters'}
            </h3>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4">Try adjusting your filters or search query.</p>
            <button
              onClick={onClearFilters}
              className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)]"
            >
              Clear filters
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
